/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Flex, Menu, Stack } from '@chakra-ui/react';
import { CaretDown, X } from '@phosphor-icons/react';

import 'react-datepicker/dist/react-datepicker.css';

import { fetchAPI } from '@/api/fetch';
import { apiCreateMockMeeting, apiGetUnscheduledCandidates } from '@/api/services';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { CandidateWithJobContext } from '@/types/Candidate';
import { Job, JobBasicInfo } from '@/types/Job';
import { Meeting } from '@/types/Meeting';
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Client } from '@/types/Client';
import { getCandidateFullName } from '@/utils';

// Types
interface FormValues {
  quality_level: {
    value: 'very_strong' | 'strong' | 'medium' | 'weak' | 'very_weak';
  };
  client?: { value: Client };
  job?: { value: Job };
  candidate?: { value: CandidateWithJobContext };
}

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700">
      {message}
    </Box>
  ) : null;

interface AddInterviewDialogProps {
  onAddInterview?: () => void;
}

export default function AddMockInterviewDialog({ onAddInterview }: AddInterviewDialogProps) {
  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<JobBasicInfo[]>([]);
  const [candidates, setCandidates] = useState<CandidateWithJobContext[]>([]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>();

  // Effects

  useEffect(() => {
    const fetchClients = async () => {
      if (clients.length > 0) return;

      try {
        const response = await fetchAPI<Client[]>({
          request: () => apiGetUnscheduledCandidates()
        });

        if (response.success) {
          setClients(response.data);
        } else {
          setSubmitError(true);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setSubmitError(true);
      }
    };

    if (isDialogOpen) {
      fetchClients();
    }
  }, [isDialogOpen]);

  // Handlers
  const handleFormSubmit = async (formData: FormValues) => {
    const { quality_level, job, candidate } = formData;

    try {
      const response = await fetchAPI<Meeting>({
        request: () =>
          apiCreateMockMeeting({
            jobId: job!.value.job_id,
            candidateId: candidate!.value.candidate_id,
            payload: {
              quality_level: quality_level.value
            }
          })
      });

      if (response.success) {
        onAddInterview?.();
        setIsDialogOpen(false);
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      console.error(`Error creating mock meeting:`, error);
      setSubmitError(true);
    }
  };

  const handleDialogClose = () => {
    reset();
    setSubmitError(false);
    setIsDialogOpen(false);
  };

  // Derived state
  const selectedClient = watch('client');
  const selectedJob = watch('job');

  return (
    <DialogRoot
      size="xl"
      open={isDialogOpen}
      onOpenChange={({ open }) => {
        setIsDialogOpen(open);
        if (open) {
          reset();
        }
      }}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogTrigger asChild>
        <Button
          color="neutrals.0"
          bg="error.500"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'error.700' }}
          aria-label="Add a new session"
        >
          Create Mock Meeting
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="12px">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Add Mock Meeting</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              {/* Quality Level Field */}
              <Field
                label={<RequiredFieldLabel>Quality Level</RequiredFieldLabel>}
                invalid={!!errors.quality_level}
                errorText={<ErrorMessage message={errors.quality_level?.message} />}
              >
                <Controller
                  name="quality_level"
                  control={control}
                  rules={{ required: 'Quality level is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          borderColor={errors.quality_level ? 'error.500' : 'neutrals.200'}
                          borderRadius="8px"
                          disabled={isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          w="100%"
                          aria-label="Select quality level"
                        >
                          <Flex justifyContent="space-between" alignItems="center" w="100%">
                            <Box
                              fontSize="16px"
                              color="neutrals.700"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                            >
                              {field.value ? `${field.value.value}` : 'Select quality level'}
                            </Box>
                            <Box as="span" ml="8px" mr="0">
                              <CaretDown size={16} aria-hidden="true" />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content
                          minW="10rem"
                          position="fixed"
                          zIndex={1500}
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Menu.RadioItemGroup
                            value={field.value?.value || ''}
                            onValueChange={(value) => {
                              field.onChange({
                                value: value.value as
                                  | 'very_strong'
                                  | 'strong'
                                  | 'medium'
                                  | 'weak'
                                  | 'very_weak'
                              });
                            }}
                          >
                            {['very_strong', 'strong', 'medium', 'weak', 'very_weak'].map(
                              (level) => (
                                <Menu.RadioItem
                                  key={level}
                                  value={level}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {level}
                                </Menu.RadioItem>
                              )
                            )}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>

              {/* Client Selection Field - Only in add mode */}
              <Field
                label={<RequiredFieldLabel>Client</RequiredFieldLabel>}
                invalid={!!errors.client}
                errorText={<ErrorMessage message={errors.client?.message} />}
              >
                <Controller
                  name="client"
                  control={control}
                  rules={{ required: 'Client is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          borderColor={errors.client ? 'error.500' : 'neutrals.200'}
                          borderRadius="8px"
                          disabled={isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          w="100%"
                          aria-label="Select client"
                        >
                          <Flex justifyContent="space-between" alignItems="center" w="100%">
                            <Box
                              fontSize="16px"
                              color="neutrals.700"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                            >
                              {field.value
                                ? `${field.value.value.unique_id} - ${field.value.value.name}`
                                : 'Select client'}
                            </Box>
                            <Box as="span" ml="8px" mr="0">
                              <CaretDown size={16} aria-hidden="true" />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content
                          minW="20rem"
                          position="fixed"
                          zIndex={1500}
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Menu.RadioItemGroup
                            value={field.value?.value.client_id || ''}
                            onValueChange={(value) => {
                              const selectedClient = clients.find(
                                (client) => client.client_id === value.value
                              );
                              field.onChange({ value: selectedClient });
                              setJobs(selectedClient?.jobs || []);
                              setValue('job', undefined);
                              setValue('candidate', undefined);
                            }}
                          >
                            {clients.map((client) => (
                              <Menu.RadioItem
                                key={client.client_id}
                                value={client.client_id}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {client.unique_id} - {client.name}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>

              {/* Job Selection Field - Only in add mode and when client is selected */}
              {selectedClient && (
                <Field
                  label={<RequiredFieldLabel>Job</RequiredFieldLabel>}
                  invalid={!!errors.job}
                  errorText={<ErrorMessage message={errors.job?.message} />}
                >
                  <Controller
                    name="job"
                    control={control}
                    rules={{ required: 'Job is required' }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            borderColor={errors.job ? 'error.500' : 'neutrals.200'}
                            borderRadius="8px"
                            disabled={isSubmitting}
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            w="100%"
                            aria-label="Select job"
                          >
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                              <Box
                                fontSize="16px"
                                color="neutrals.700"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                              >
                                {field.value
                                  ? `${field.value.value.unique_id} - ${field.value.value.job_title}`
                                  : 'Select job'}
                              </Box>
                              <Box as="span" ml="8px" mr="0">
                                <CaretDown size={16} aria-hidden="true" />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content
                            minW="20rem"
                            w="fit-content"
                            position="fixed"
                            zIndex={1500}
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Menu.RadioItemGroup
                              value={field.value?.value.job_id || ''}
                              onValueChange={(value) => {
                                const selectedJob = jobs.find((job) => job.job_id === value.value);
                                field.onChange({ value: selectedJob });
                                setCandidates(selectedJob?.candidates || []);
                                setValue('candidate', undefined);
                              }}
                            >
                              {jobs.map((job) => (
                                <Menu.RadioItem
                                  key={job.job_id}
                                  value={job.job_id}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {job.job_title}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                </Field>
              )}

              {/* Candidate Selection Field - Only in add mode and when job is selected */}
              {selectedJob && (
                <Field
                  label={<RequiredFieldLabel>Candidate</RequiredFieldLabel>}
                  invalid={!!errors.candidate}
                  errorText={<ErrorMessage message={errors.candidate?.message} />}
                >
                  <Controller
                    name="candidate"
                    control={control}
                    rules={{ required: 'Candidate is required' }}
                    render={({ field }) => (
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <Button
                            variant="outline"
                            size="lg"
                            borderColor={errors.candidate ? 'error.500' : 'neutrals.200'}
                            borderRadius="8px"
                            disabled={isSubmitting}
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            w="100%"
                            aria-label="Select candidate"
                          >
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                              <Box
                                fontSize="16px"
                                color="neutrals.700"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                              >
                                {field.value
                                  ? getCandidateFullName(
                                      field.value.value.first_name,
                                      field.value.value.last_name
                                    )
                                  : 'Select candidate'}
                              </Box>
                              <Box as="span" ml="8px" mr="0">
                                <CaretDown size={16} aria-hidden="true" />
                              </Box>
                            </Flex>
                          </Button>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content
                            minW="20rem"
                            position="fixed"
                            zIndex={1500}
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Menu.RadioItemGroup
                              value={field.value?.value.candidate_id || ''}
                              onValueChange={(value) => {
                                const selectedCandidate = candidates.find(
                                  (candidate) => candidate.candidate_id === value.value
                                );
                                field.onChange({ value: selectedCandidate });
                              }}
                            >
                              {candidates.map((candidate) => (
                                <Menu.RadioItem
                                  key={candidate.candidate_id}
                                  value={candidate.candidate_id}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {getCandidateFullName(candidate.first_name, candidate.last_name)}{' '}
                                  - {candidate.email}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    )}
                  />
                </Field>
              )}
            </Stack>

            {/* Error Message */}
            {submitError && (
              <Box color="error.700" fontSize="16px" mt="16px">
                Failed to create mock meeting. Please try again.
              </Box>
            )}
          </DialogBody>

          {/* Dialog Footer */}
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w="160px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                disabled={isSubmitting}
                aria-label="Cancel"
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w="160px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              loading={isSubmitting}
              aria-label="Submit"
            >
              Submit
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting}>
          <X size={32} weight="bold" aria-label="Close dialog" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
